import {
  COMPLIANCE_LOG_OUTCOMES,
  COMPLIANCE_LOG_OUTCOME_LABELS,
  type ComplianceLogOutcome,
} from '@sitewise/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  LoadingState,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { listBuildings } from '../../../api/buildings.js';
import {
  confirmComplianceIngestJob,
  createComplianceIngestJob,
  listComplianceIngestJobs,
  listDevices,
  type ComplianceIngestJob,
} from '../../../api/compliance.js';
import { presignUpload } from '../../../api/uploads.js';
import { ApiError } from '../../../lib/api-client.js';

export function ComplianceIngestPage() {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const jobsQuery = useQuery({
    queryKey: ['compliance', 'ingest'],
    queryFn: listComplianceIngestJobs,
  });

  async function handleUpload(file: File) {
    setUploading(true);
    try {
      const presigned = await presignUpload({
        kind: 'compliance_ingest',
        filename: file.name,
        contentType: file.type || 'application/pdf',
        sizeBytes: file.size,
      });
      const put = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: presigned.headers,
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      const { job } = await createComplianceIngestJob({
        uploadId: presigned.uploadId,
        filename: file.name,
      });
      toast.success(
        job.status === 'error'
          ? 'Uploaded, but parsing failed — open the job for details'
          : 'Certificate parsed — confirm the match',
      );
      setSelectedJobId(job.id);
      void queryClient.invalidateQueries({ queryKey: ['compliance', 'ingest'] });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const jobs = jobsQuery.data?.jobs ?? [];
  const selected = jobs.find((j) => j.id === selectedJobId) ?? null;

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_1.1fr]">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-base">Upload inbox</CardTitle>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,.pdf,application/pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleUpload(file);
              }}
            />
            <Button
              size="sm"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              <FileUp className="h-3.5 w-3.5" />
              {uploading ? 'Parsing…' : 'Upload certificate'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-fg-muted">
            Drop a boiler, elevator, RPZ, or other inspection PDF. SiteWise reads it and asks you
            to confirm building, device, and dates before applying the log.
          </p>
          {jobsQuery.isLoading ? (
            <LoadingState rows={4} />
          ) : jobs.length === 0 ? (
            <p className="text-sm text-fg-muted">No uploads yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {jobs.map((job) => (
                <li key={job.id}>
                  <button
                    type="button"
                    className={`flex w-full items-start justify-between gap-2 py-3 text-left ${
                      selectedJobId === job.id ? 'bg-surface-muted/50' : ''
                    }`}
                    onClick={() => setSelectedJobId(job.id)}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{job.filename}</div>
                      <div className="text-xs text-fg-muted">
                        {job.status}
                        {job.confidence != null
                          ? ` · ${Math.round(job.confidence * 100)}%`
                          : ''}
                        {' · '}
                        {new Date(job.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {selected ? (
        <ConfirmIngestPanel
          job={selected}
          onApplied={() => {
            void queryClient.invalidateQueries({ queryKey: ['compliance'] });
            setSelectedJobId(selected.id);
          }}
        />
      ) : (
        <Card>
          <CardContent className="p-6 text-sm text-fg-muted">
            Select an upload to review the AI suggestion and apply it to a device.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ConfirmIngestPanel({
  job,
  onApplied,
}: {
  job: ComplianceIngestJob;
  onApplied: () => void;
}) {
  const devicesQuery = useQuery({
    queryKey: ['compliance', 'devices', 'ingest'],
    queryFn: () => listDevices({}),
  });
  const buildingsQuery = useQuery({
    queryKey: ['buildings', 'ingest'],
    queryFn: () => listBuildings({ limit: 100 }),
  });

  const suggestion = job.suggestion;
  const [deviceId, setDeviceId] = useState(suggestion?.deviceId ?? '');
  const [performedOn, setPerformedOn] = useState(suggestion?.performedOn ?? '');
  const [outcome, setOutcome] = useState<ComplianceLogOutcome>(
    (suggestion?.outcome as ComplianceLogOutcome | null) ?? 'passed',
  );
  const [nextDue, setNextDue] = useState(suggestion?.suggestedNextDueOn ?? '');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    setDeviceId(job.suggestion?.deviceId ?? '');
    setPerformedOn(job.suggestion?.performedOn ?? '');
    setOutcome((job.suggestion?.outcome as ComplianceLogOutcome | null) ?? 'passed');
    setNextDue(job.suggestion?.suggestedNextDueOn ?? '');
    setNotes('');
  }, [job.id, job.suggestion]);

  const confirmMutation = useMutation({
    mutationFn: () =>
      confirmComplianceIngestJob(job.id, {
        deviceId,
        performedOn,
        outcome,
        notes: notes.trim() || null,
        nextDueOverride: nextDue || null,
      }),
    onSuccess: () => {
      toast.success('Certificate applied to compliance log');
      onApplied();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not apply certificate');
    },
  });

  const devices = devicesQuery.data?.devices ?? [];
  const buildingName = (id: string | null | undefined) =>
    buildingsQuery.data?.buildings.find((b) => b.id === id)?.name ?? id;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Confirm apply</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {job.status === 'error' ? (
          <p className="text-sm text-danger">{job.errorMessage ?? 'Parse failed'}</p>
        ) : null}
        {suggestion ? (
          <div className="rounded border border-border bg-surface-muted/40 px-3 py-2 text-sm">
            <p>{suggestion.summary}</p>
            <p className="mt-1 text-xs text-fg-muted">
              Suggested: {buildingName(suggestion.buildingId)} · device{' '}
              {devices.find((d) => d.id === suggestion.deviceId)?.label ?? '—'} ·{' '}
              {Math.round(suggestion.confidence * 100)}% confidence
            </p>
          </div>
        ) : null}

        {job.status === 'applied' ? (
          <p className="text-sm text-success">Already applied to a compliance log.</p>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!deviceId || !performedOn) {
                toast.error('Device and performed-on date are required');
                return;
              }
              confirmMutation.mutate();
            }}
          >
            <div className="grid gap-1.5">
              <Label>Device</Label>
              <select
                className="h-10 rounded-md border border-border bg-surface px-3 text-sm"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                required
              >
                <option value="">Select device…</option>
                {devices.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.label} · {d.deviceType.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Performed on</Label>
                <Input
                  type="date"
                  value={performedOn}
                  onChange={(e) => setPerformedOn(e.target.value)}
                  required
                />
              </div>
              <div className="grid gap-1.5">
                <Label>Outcome</Label>
                <select
                  className="h-10 rounded-md border border-border bg-surface px-3 text-sm"
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value as ComplianceLogOutcome)}
                >
                  {COMPLIANCE_LOG_OUTCOMES.map((o) => (
                    <option key={o} value={o}>
                      {COMPLIANCE_LOG_OUTCOME_LABELS[o]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>Next due override (optional)</Label>
              <Input type="date" value={nextDue} onChange={(e) => setNextDue(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Notes (optional)</Label>
              <textarea
                rows={3}
                className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={confirmMutation.isPending || job.status === 'error'}>
              {confirmMutation.isPending ? 'Applying…' : 'Apply to compliance record'}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
